import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

import { env } from "@/config/env";
import {
  ymdAddDaysInAppointmentZone,
  ymdTodayInAppointmentZone,
} from "@/lib/appointmentCalendar";

/**
 * IANA zone used for stored slots & weekly availability (matches
 * appointment-service `chikitsalink.appointment.zone`).
 *
 * This is the **fallback** — the authoritative source is the backend
 * response (e.g. {@link Slot#timeZone}, {@link Appointment#timeZone} or
 * {@code GET /api/appointments/config}). Prefer {@link clinicTimeZoneOf}
 * below when a server-supplied zone is available.
 */
export function clinicTimeZone(): string {
  return env.appointmentCalendarZone;
}

/** Returns the server-supplied zone if present, else the env fallback. */
export function clinicTimeZoneOf(serverZone?: string | null): string {
  return serverZone && serverZone.length > 0
    ? serverZone
    : env.appointmentCalendarZone;
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

function padTimeToHhMmSs(hhmmOrHhmmss: string): string {
  if (hhmmOrHhmmss.length === 5) return `${hhmmOrHhmmss}:00`;
  return hhmmOrHhmmss;
}

/**
 * Instant for a calendar wall-clock in the given clinic zone (e.g. India
 * IST for telemedicine). {@code zone} defaults to the env fallback so
 * callers can pass `undefined` safely.
 */
export function instantFromClinicWall(
  ymd: string,
  hhmmOrHhmmss: string,
  zone?: string | null,
): Date {
  const t = padTimeToHhMmSs(hhmmOrHhmmss);
  return fromZonedTime(`${ymd}T${t}`, clinicTimeZoneOf(zone));
}

export function formatInstantInBrowserZone(instant: Date, pattern: string): string {
  return formatInTimeZone(instant, browserTimeZone(), pattern);
}

export function formatInstantInClinicZone(
  instant: Date,
  pattern: string,
  zone?: string | null,
): string {
  return formatInTimeZone(instant, clinicTimeZoneOf(zone), pattern);
}

/** UTC ISO string for an appointment boundary built from clinic-local date + time. */
export function isoInstantFromClinicDateAndTime(
  ymd: string,
  hhmmOrHhmmss: string,
  zone?: string | null,
): string {
  return instantFromClinicWall(ymd, hhmmOrHhmmss, zone).toISOString();
}

/** Short zone label used in hints (e.g. `IST` for Asia/Kolkata). */
export function clinicZoneShortLabel(zone?: string | null): string {
  const z = clinicTimeZoneOf(zone);
  if (z === "Asia/Kolkata" || z === "Asia/Calcutta") return "IST";
  return z;
}

/** True when the browser zone and clinic zone are the same IANA id. */
export function browserMatchesClinicZone(zone?: string | null): boolean {
  return browserTimeZone() === clinicTimeZoneOf(zone);
}

function isoDowOfYmdInClinic(ymd: string, zone?: string | null): number {
  return Number(
    formatInTimeZone(
      fromZonedTime(`${ymd}T12:00:00`, clinicTimeZoneOf(zone)),
      clinicTimeZoneOf(zone),
      "i",
    ),
  );
}

/**
 * Next clinic-calendar date (within ~3 weeks) on or after "today" in the
 * clinic zone whose ISO weekday matches {@code dow} (1 = Monday … 7 = Sunday).
 */
export function anchorYmdForIsoDowInClinic(
  dow: number,
  now = new Date(),
  zone?: string | null,
): string {
  const start = ymdTodayInAppointmentZone(now);
  for (let i = 0; i < 21; i++) {
    const ymd = ymdAddDaysInAppointmentZone(start, i);
    if (isoDowOfYmdInClinic(ymd, zone) === dow) return ymd;
  }
  return start;
}

/**
 * Format clinic HH:mm as the same absolute instant in the browser zone.
 *
 * <b>Use for read-only hints only.</b> Editing a time in the browser zone
 * and converting back can silently shift the day-of-week when the browser
 * and clinic zone cross midnight, which has caused confusion in the past.
 * The canonical editing page (`DoctorSchedulePage`) edits clinic HH:mm
 * directly and uses this helper only to print a reference line.
 */
export function clinicHHmmToBrowserHHmmHint(
  dow: number,
  clinicHhmm: string,
  zone?: string | null,
): string {
  const ymd = anchorYmdForIsoDowInClinic(dow, new Date(), zone);
  return formatInTimeZone(
    instantFromClinicWall(ymd, clinicHhmm, zone),
    browserTimeZone(),
    "HH:mm",
  );
}

/**
 * Compute the ISO weekday (1–7) that the given clinic HH:mm on the given
 * {@code dow} lands on in the browser zone. Differs from {@code dow} only
 * when the conversion crosses local midnight.
 */
export function clinicHHmmBrowserDow(
  dow: number,
  clinicHhmm: string,
  zone?: string | null,
): number {
  const ymd = anchorYmdForIsoDowInClinic(dow, new Date(), zone);
  const iso = instantFromClinicWall(ymd, clinicHhmm, zone);
  return Number(formatInTimeZone(iso, browserTimeZone(), "i"));
}

/**
 * Short human-friendly weekday name ("Mon"…"Sun") from an ISO-8601 weekday
 * number (1 = Monday … 7 = Sunday), in English so it's unambiguous.
 */
export function isoDowShortName(dow: number): string {
  const names = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return names[dow] ?? String(dow);
}
